/**
 * RESEARCH MISSIONS — the Owner's durable mandate, with the real controls.
 *
 * A mission is not a window and not a schedule: it names the question the
 * engine grounds, an explicit duration, and the PUBLIC terms each search batch
 * may send. Continue and Pause are the two Owner acts the HTTP surface
 * supports, and they are shown only where they apply.
 */

import { useEffect, useState } from 'preact/hooks';
import { PageHeader } from './about';
import {
	authorizeResearchMission,
	continueResearchMission,
	listResearchMissions,
	pauseResearchMission,
	type ResearchMission,
} from '../missions';

function reasonOf(error: { message: string } | null | undefined): string {
	return error?.message ?? 'the request failed without a reason';
}

export function MissionsView() {
	const [missions, setMissions] = useState<ResearchMission[]>([]);
	const [notice, setNotice] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [question, setQuestion] = useState('');
	const [instruction, setInstruction] = useState('');
	const [duration, setDuration] = useState('30');
	const [subjects, setSubjects] = useState('');

	useEffect(() => {
		void refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	async function refresh(): Promise<void> {
		const result = await listResearchMissions();
		if (!result.ok) {
			setNotice(reasonOf(result.error));
			return;
		}
		setMissions(result.data.missions ?? []);
	}

	async function authorize(): Promise<void> {
		const batches = subjects
			.split(/\n+/)
			.map((line) =>
				line
					.split(',')
					.map((term) => term.trim())
					.filter((term) => term.length > 0),
			)
			.filter((batch) => batch.length > 0);
		if (question.trim().length === 0 || batches.length === 0) {
			setNotice('A mission needs a question and at least one public search batch.');
			return;
		}
		const minutes = Number(duration);
		if (!Number.isFinite(minutes) || minutes < 5 || minutes > 1_440) {
			setNotice('The mission duration must be between 5 and 1,440 minutes.');
			return;
		}
		setBusy(true);
		const result = await authorizeResearchMission({
			question: question.trim(),
			...(instruction.trim().length === 0 ? {} : { instruction: instruction.trim() }),
			durationMinutes: minutes,
			publicSubjects: batches,
		});
		setBusy(false);
		if (!result.ok) {
			setNotice(reasonOf(result.error));
			return;
		}
		setNotice(
			`Mission ${result.data.mission?.missionId ?? ''} authorized until ${result.data.mission?.deadlineAt ?? 'its deadline'}.`,
		);
		await refresh();
	}

	async function act(missionId: string, action: 'continue' | 'pause'): Promise<void> {
		setBusy(true);
		const result =
			action === 'continue'
				? await continueResearchMission(missionId)
				: await pauseResearchMission(missionId);
		setBusy(false);
		if (!result.ok) {
			setNotice(reasonOf(result.error));
			return;
		}
		// A 200 IS NOT AN ACCEPTED ACT. The gateway reports a refused pause as
		// `{paused:false, reason}` and a refused continuation as `{ok:false,
		// reason}` inside a successful envelope; reading only the HTTP status made
		// the screen say "pause accepted" while the mission stayed ACTIVE. This is
		// the defect the browser interaction specimen found.
		const refused =
			action === 'pause'
				? 'paused' in result.data && result.data.paused === false
				: result.data.ok === false;
		if (refused) {
			setNotice(result.data.reason ?? `${missionId}: ${action} was refused; the mission is unchanged.`);
			await refresh();
			return;
		}
		setNotice(`${missionId}: ${action} accepted.`);
		await refresh();
	}

	return (
		<section class="view" aria-labelledby="missions-heading">
			<PageHeader
				id="missions-heading"
				title="Research missions"
				about={{
					purpose:
						'A durable research mandate: the question, the authorized duration and the public search plan the engine works inside.',
					limits: (
						<p>
							A mission is not an unlimited budget. Its deadline and its declared
							public search batches are the authority; Scout decides what to investigate
							inside them.
						</p>
					),
				}}
			/>
			{notice !== null && (
				<p class="panel small" role="status" style="padding:8px 12px;">
					{notice}
				</p>
			)}
			<h2>On record</h2>
			{missions.length === 0 ? (
				<p class="muted">No research mission is on record for this owner.</p>
			) : (
				<ul class="list">
					{missions.map((mission) => (
						<li key={mission.missionId} class="panel" style="padding:10px; margin:8px 0;">
							<div>
								<strong>{mission.missionId}</strong> <code class="small">{mission.status}</code>
								{mission.outcome === null || mission.outcome === undefined ? null : (
									<span class="small muted"> · outcome {mission.outcome}</span>
								)}
							</div>
							<div class="small">{mission.question ?? mission.instruction ?? ''}</div>
							<div class="small muted">deadline {mission.deadlineAt}</div>
							<div style="display:flex; gap:8px; flex-wrap:wrap; margin-top:8px;">
								{mission.status === 'ACTIVE' && (
									<>
										<button
											type="button"
											class="button ghost"
											disabled={busy}
											onClick={() => void act(mission.missionId, 'continue')}
										>
											Continue one batch
										</button>
										<button
											type="button"
											class="button ghost"
											disabled={busy}
											onClick={() => void act(mission.missionId, 'pause')}
										>
											Pause
										</button>
									</>
								)}
							</div>
						</li>
					))}
				</ul>
			)}
			<h2>Authorize a mission</h2>
			<p class="small muted">
				The public subjects are the terms each search batch may send. One batch per
				line; comma-separated terms within a line. Private context is not accepted here.
			</p>
			<label style="display:block; margin:8px 0;">
				<span class="small muted">Question the engine grounds</span>
				<textarea
					rows={3}
					value={question}
					onInput={(event) => setQuestion((event.target as HTMLTextAreaElement).value)}
					style="display:block; width:100%;"
				/>
			</label>
			<label style="display:block; margin:8px 0;">
				<span class="small muted">Instruction (optional)</span>
				<textarea
					rows={2}
					value={instruction}
					onInput={(event) => setInstruction((event.target as HTMLTextAreaElement).value)}
					style="display:block; width:100%;"
				/>
			</label>
			<label style="display:block; margin:8px 0;">
				<span class="small muted">Duration (minutes, 5–1440)</span>
				<input
					type="number"
					min="5"
					max="1440"
					value={duration}
					onInput={(event) => setDuration((event.target as HTMLInputElement).value)}
				/>
			</label>
			<label style="display:block; margin:8px 0;">
				<span class="small muted">Public search batches</span>
				<textarea
					rows={4}
					value={subjects}
					onInput={(event) => setSubjects((event.target as HTMLTextAreaElement).value)}
					placeholder={'sqlite wal mode, sqlite checkpoint\nsqlite without rowid'}
					style="display:block; width:100%;"
				/>
			</label>
			<button type="button" class="button" disabled={busy} onClick={() => void authorize()}>
				Authorize mission
			</button>
		</section>
	);
}
